import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// English translations
const en = {
  // Common
  app_name: "Wanderer's Wallet",
  loading: "Loading...",
  save: "Save",
  cancel: "Cancel",
  delete: "Delete",
  edit: "Edit",
  close: "Close",
  next: "Next",
  back: "Back",
  confirm: "Confirm",
  
  // Navigation
  nav_today: "Today",
  nav_history: "History",
  nav_settings: "Settings",
  
  // Onboarding
  welcome: "Welcome to Wanderer's Wallet",
  welcome_subtitle: "Let's protect your ability to keep traveling",
  total_budget: "Total Trip Budget",
  trip_duration: "Trip Duration",
  trip_duration_subtitle: "How long is your trip?",
  days: "days",
  where_going: "Where are you going?",
  destination_placeholder: "Enter destination",
  all_set: "All Set!",
  review_budget: "Review your budget before starting",
  daily_budget_label: "Your Daily Budget",
  start_adventure: "Start My Adventure",
  
  // Today Screen
  daily_budget: "Daily Budget",
  spent_today: "Spent Today",
  remaining: "Remaining",
  add_expense: "Add Expense",
  understand_runway: "Understand Your Runway",
  runway_remaining: "{{days}} days runway remaining",
  
  // Quick Converter
  quick_price_check: "Quick Price Check",
  converter_subtitle: "Convert prices instantly • Add as expense if needed",
  enter_amount: "Enter amount",
  converts_to: "Converts to",
  add_as_expense: "Add as Expense",
  
  // Expenses
  expense_categories: {
    food: "Food",
    transport: "Transport",
    accommodation: "Accommodation",
    activities: "Activities",
    shopping: "Shopping",
    other: "Other"
  },
  add_note: "Add a note (optional)",
  
  // AI Suggestions
  ai_suggestions: "AI Suggestions",
  ai_suggestions_subtitle: "Budget-aware recommendations",
  food_suggestions: "Food",
  lodging_suggestions: "Lodging",
  transport_suggestions: "Transport",
  search_radius: "Search Radius",
  miles: "miles",
  
  // Scam Alerts
  scam_alerts: "Scam Alerts",
  stay_safe_in: "Stay Safe in {{destination}}",
  common_scams: "Common Scams",
  how_to_avoid: "How to Avoid",
  red_flags: "Red Flags",
  emergency_contacts: "Emergency Contacts",
  high_risk: "High Risk",
  medium_risk: "Medium Risk",
  low_risk: "Low Risk",
  view_safety_tips: "View Safety Tips",
  
  // eSIM
  need_mobile_data: "Need Mobile Data?",
  esim_subtitle: "Get instant eSIM data for {{destination}}",
  get_esim: "Get eSIM for 200+ Countries",
  esim_powered_by: "Powered by Airalo • 200+ countries",
  
  // Settings
  settings: "Settings",
  manage_preferences: "Manage your preferences",
  premium_status: "Premium Status",
  premium_active: "Premium Active",
  free_plan: "Free Plan",
  upgrade_premium: "Upgrade to Premium",
  current_trip: "Current Trip",
  destination: "Destination",
  budget_settings: "Budget Settings",
  default_currency: "Default Currency",
  
  // Referral
  referral_program: "Referral Program",
  your_referral_code: "Your Referral Code",
  generate_code: "Generate",
  share_code_subtitle: "Share your code to get 2 weeks premium",
  redeem_referral: "Redeem Referral Code",
  enter_code: "Enter code",
  redeem: "Redeem",
  
  // Data Management
  data_management: "Data Management",
  reset_trip: "Reset Trip Budget",
  export_data: "Export Data (JSON)",
  delete_all: "Delete All Data",
  
  // Premium Features
  premium_feature: "Premium Feature",
  unlock_premium: "Unlock with Premium",
  themes: "Themes",
  customize_theme: "Customize Theme",
  
  // Misc
  last_updated: "Last updated",
  powered_by: "Powered by",
  data_stored_locally: "Your data is stored locally on your device."
};

// Spanish translations
const es = {
  // Common
  app_name: "Wanderer's Wallet",
  loading: "Cargando...",
  save: "Guardar",
  cancel: "Cancelar",
  delete: "Eliminar",
  edit: "Editar",
  close: "Cerrar",
  next: "Siguiente",
  back: "Atrás",
  confirm: "Confirmar",
  
  // Navigation
  nav_today: "Hoy",
  nav_history: "Historial",
  nav_settings: "Ajustes",
  
  // Onboarding
  welcome: "Bienvenido a Wanderer's Wallet",
  welcome_subtitle: "Protejamos tu capacidad de seguir viajando",
  total_budget: "Presupuesto Total del Viaje",
  trip_duration: "Duración del Viaje",
  trip_duration_subtitle: "¿Cuánto dura tu viaje?",
  days: "días",
  where_going: "¿A dónde vas?",
  destination_placeholder: "Ingresa destino",
  all_set: "¡Todo Listo!",
  review_budget: "Revisa tu presupuesto antes de comenzar",
  daily_budget_label: "Tu Presupuesto Diario",
  start_adventure: "Comenzar Mi Aventura",
  
  // Today Screen
  daily_budget: "Presupuesto Diario",
  spent_today: "Gastado Hoy",
  remaining: "Restante",
  add_expense: "Agregar Gasto",
  understand_runway: "Entender Tu Margen",
  runway_remaining: "{{days}} días de margen restantes",
  
  // Quick Converter
  quick_price_check: "Verificar Precio Rápido",
  converter_subtitle: "Convierte precios al instante • Agregar como gasto si necesitas",
  enter_amount: "Ingresa cantidad",
  converts_to: "Se convierte a",
  add_as_expense: "Agregar como Gasto",
  
  // Expenses
  expense_categories: {
    food: "Comida",
    transport: "Transporte",
    accommodation: "Alojamiento",
    activities: "Actividades",
    shopping: "Compras",
    other: "Otro"
  },
  add_note: "Agregar nota (opcional)",
  
  // AI Suggestions
  ai_suggestions: "Sugerencias IA",
  ai_suggestions_subtitle: "Recomendaciones según tu presupuesto",
  food_suggestions: "Comida",
  lodging_suggestions: "Alojamiento",
  transport_suggestions: "Transporte",
  search_radius: "Radio de Búsqueda",
  miles: "millas",
  
  // Scam Alerts
  scam_alerts: "Alertas de Estafas",
  stay_safe_in: "Mantente Seguro en {{destination}}",
  common_scams: "Estafas Comunes",
  how_to_avoid: "Cómo Evitar",
  red_flags: "Señales de Alerta",
  emergency_contacts: "Contactos de Emergencia",
  high_risk: "Alto Riesgo",
  medium_risk: "Riesgo Medio",
  low_risk: "Bajo Riesgo",
  view_safety_tips: "Ver Consejos de Seguridad",
  
  // eSIM
  need_mobile_data: "¿Necesitas Datos Móviles?",
  esim_subtitle: "Obtén eSIM instantánea para {{destination}}",
  get_esim: "Obtener eSIM para 200+ Países",
  esim_powered_by: "Airalo • eSIM para 200+ países",
  
  // Settings
  settings: "Ajustes",
  manage_preferences: "Administra tus preferencias",
  premium_status: "Estado Premium",
  premium_active: "Premium Activo",
  free_plan: "Plan Gratuito",
  upgrade_premium: "Mejorar a Premium",
  current_trip: "Viaje Actual",
  destination: "Destino",
  budget_settings: "Ajustes de Presupuesto",
  default_currency: "Moneda Predeterminada",
  
  // Referral
  referral_program: "Programa de Referidos",
  your_referral_code: "Tu Código de Referido",
  generate_code: "Generar",
  share_code_subtitle: "Comparte tu código para obtener 2 semanas premium",
  redeem_referral: "Canjear Código de Referido",
  enter_code: "Ingresa código",
  redeem: "Canjear",
  
  // Data Management
  data_management: "Gestión de Datos",
  reset_trip: "Reiniciar Presupuesto de Viaje",
  export_data: "Exportar Datos (JSON)",
  delete_all: "Eliminar Todos los Datos",
  
  // Premium Features
  premium_feature: "Función Premium",
  unlock_premium: "Desbloquear con Premium",
  themes: "Temas",
  customize_theme: "Personalizar Tema",
  
  // Misc
  last_updated: "Última actualización",
  powered_by: "Con tecnología de",
  data_stored_locally: "Tus datos se almacenan localmente en tu dispositivo."
};

// Chinese (Simplified) translations
const zh = {
  // Common
  app_name: "旅行者钱包",
  loading: "加载中...",
  save: "保存",
  cancel: "取消",
  delete: "删除",
  edit: "编辑",
  close: "关闭",
  next: "下一步",
  back: "返回",
  confirm: "确认",
  
  // Navigation
  nav_today: "今天",
  nav_history: "历史",
  nav_settings: "设置",
  
  // Onboarding
  welcome: "欢迎使用旅行者钱包",
  welcome_subtitle: "让我们保护你继续旅行的能力",
  total_budget: "旅行总预算",
  trip_duration: "旅行时长",
  trip_duration_subtitle: "你的旅行有多长？",
  days: "天",
  where_going: "你要去哪里？",
  destination_placeholder: "输入目的地",
  all_set: "准备就绪！",
  review_budget: "开始前检查你的预算",
  daily_budget_label: "你的每日预算",
  start_adventure: "开始我的冒险",
  
  // Today Screen
  daily_budget: "每日预算",
  spent_today: "今日支出",
  remaining: "剩余",
  add_expense: "添加支出",
  understand_runway: "了解你的资金跑道",
  runway_remaining: "还剩 {{days}} 天资金",
  
  // Quick Converter
  quick_price_check: "快速价格检查",
  converter_subtitle: "即时转换价格 • 可添加为支出",
  enter_amount: "输入金额",
  converts_to: "转换为",
  add_as_expense: "添加为支出",
  
  // Expenses
  expense_categories: {
    food: "餐饮",
    transport: "交通",
    accommodation: "住宿",
    activities: "活动",
    shopping: "购物",
    other: "其他"
  },
  add_note: "添加备注（可选）",
  
  // AI Suggestions
  ai_suggestions: "AI 建议",
  ai_suggestions_subtitle: "基于预算的推荐",
  food_suggestions: "餐饮",
  lodging_suggestions: "住宿",
  transport_suggestions: "交通",
  search_radius: "搜索半径",
  miles: "英里",
  
  // Scam Alerts
  scam_alerts: "诈骗警报",
  stay_safe_in: "在{{destination}}保持安全",
  common_scams: "常见骗局",
  how_to_avoid: "如何避免",
  red_flags: "危险信号",
  emergency_contacts: "紧急联系人",
  high_risk: "高风险",
  medium_risk: "中等风险",
  low_risk: "低风险",
  view_safety_tips: "查看安全提示",
  
  // eSIM
  need_mobile_data: "需要移动数据？",
  esim_subtitle: "为{{destination}}获取即时eSIM",
  get_esim: "获取200+国家eSIM",
  esim_powered_by: "Airalo • 200+国家eSIM",
  
  // Settings
  settings: "设置",
  manage_preferences: "管理你的偏好",
  premium_status: "高级状态",
  premium_active: "高级版已激活",
  free_plan: "免费计划",
  upgrade_premium: "升级到高级版",
  current_trip: "当前旅行",
  destination: "目的地",
  budget_settings: "预算设置",
  default_currency: "默认货币",
  
  // Referral
  referral_program: "推荐计划",
  your_referral_code: "你的推荐码",
  generate_code: "生成",
  share_code_subtitle: "分享你的代码获得2周高级版",
  redeem_referral: "兑换推荐码",
  enter_code: "输入代码",
  redeem: "兑换",
  
  // Data Management
  data_management: "数据管理",
  reset_trip: "重置旅行预算",
  export_data: "导出数据 (JSON)",
  delete_all: "删除所有数据",
  
  // Premium Features
  premium_feature: "高级功能",
  unlock_premium: "使用高级版解锁",
  themes: "主题",
  customize_theme: "自定义主题",
  
  // Misc
  last_updated: "最后更新",
  powered_by: "技术支持",
  data_stored_locally: "你的数据存储在本地设备上。"
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      zh: { translation: zh }
    },
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['navigator', 'localStorage', 'htmlTag'],
      caches: ['localStorage']
    }
  });

export default i18n;
